import { WarningAlt } from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Icon,
  LoadingIcon,
} from 'vet-bones/design-system/components';

import { CopyableInput } from 'src/ui/components/CopyableInput';
import {
  CONFIGURE_ORG_SSO_DIALOG_DELETE_CANCEL,
  CONFIGURE_ORG_SSO_DIALOG_DELETE_CONFIRM,
  CONFIGURE_ORG_SSO_DIALOG_LOGIN_URL,
  CONFIGURE_ORG_SSO_DIALOG_LOGIN_URL_COPY,
  CONFIGURE_ORG_SSO_DIALOG_POST_LOGOUT_URI,
  CONFIGURE_ORG_SSO_DIALOG_POST_LOGOUT_URI_COPY,
  CONFIGURE_ORG_SSO_DIALOG_REDIRECT_URI,
  CONFIGURE_ORG_SSO_DIALOG_REDIRECT_URI_COPY,
} from 'src/ui/constants/testIds';
import { SsoProviderType } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

const PROVIDER_LABELS: Record<SsoProviderType, string> = {
  [SsoProviderType.Okta]: copy.components.configureOrgSSO.dialog.providers.okta,
  [SsoProviderType.MicrosoftEntra]:
    copy.components.configureOrgSSO.dialog.providers.microsoftEntra,
};

interface ConfigureOrgSSOViewProps {
  config: {
    providerType: SsoProviderType;
    loginUrl: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    idpLogoutEnabled: boolean;
  };
  isDeleting: boolean;
  onDelete: () => void;
  onShowDeleteConfirm: (show: boolean) => void;
  orgDomain: string;
  showDeleteConfirm: boolean;
}

export const ConfigureOrgSSOView: React.VFC<ConfigureOrgSSOViewProps> = ({
  config,
  isDeleting,
  onDelete,
  onShowDeleteConfirm,
  orgDomain,
  showDeleteConfirm,
}) => (
  <>
    <div className="organization-dialog-callout">
      <Body className="organization-dialog-container-header" size="md">
        {copy.components.configureOrgSSO.dialog.view.provider.label}
      </Body>
      <Body size="lg" weight="semibold">
        {PROVIDER_LABELS[config.providerType]}
      </Body>
    </div>
    <div className="organization-dialog-container">
      <Body className="organization-dialog-container-header" size="lg">
        {copy.components.configureOrgSSO.dialog.view.loginUrl.label}
      </Body>
      <Body className="organization-dialog-container-header" size="sm">
        {copy.components.configureOrgSSO.dialog.view.loginUrl.help(orgDomain)}
      </Body>
    </div>
    <CopyableInput
      buttonTestId={CONFIGURE_ORG_SSO_DIALOG_LOGIN_URL_COPY}
      inputTestId={CONFIGURE_ORG_SSO_DIALOG_LOGIN_URL}
      label=""
      value={config.loginUrl}
    />
    <div className="organization-dialog-container">
      <Body className="organization-dialog-container-header" size="lg">
        {copy.components.configureOrgSSO.dialog.view.idpConfig.label}
      </Body>
      <Body className="organization-dialog-container-header" size="sm">
        {copy.components.configureOrgSSO.dialog.view.idpConfig.help}
      </Body>
    </div>
    <CopyableInput
      buttonTestId={CONFIGURE_ORG_SSO_DIALOG_REDIRECT_URI_COPY}
      inputTestId={CONFIGURE_ORG_SSO_DIALOG_REDIRECT_URI}
      label={copy.components.configureOrgSSO.dialog.view.redirectUri.label}
      value={config.redirectUri}
    />
    {config.idpLogoutEnabled ? (
      <CopyableInput
        buttonTestId={CONFIGURE_ORG_SSO_DIALOG_POST_LOGOUT_URI_COPY}
        inputTestId={CONFIGURE_ORG_SSO_DIALOG_POST_LOGOUT_URI}
        label={
          copy.components.configureOrgSSO.dialog.view.postLogoutRedirectUri
            .label
        }
        value={config.postLogoutRedirectUri}
      />
    ) : null}
    {showDeleteConfirm ? (
      <>
        <div className="organization-dialog-warning">
          <Icon IconComponent={WarningAlt} size="lg" />
          <div>
            <Body size="md">
              {
                copy.components.configureOrgSSO.dialog.view.delete.warning
                  .line_1
              }
            </Body>
            <Body size="md">
              {
                copy.components.configureOrgSSO.dialog.view.delete.warning
                  .line_2
              }
            </Body>
          </div>
        </div>
        <div className="organization-dialog-actions">
          <Button
            disabled={isDeleting}
            onClick={onDelete}
            rightIcon={isDeleting ? LoadingIcon : undefined}
            size="sm"
            testId={CONFIGURE_ORG_SSO_DIALOG_DELETE_CONFIRM}
            variant="error"
          >
            {copy.components.configureOrgSSO.dialog.view.delete.submit}
          </Button>
          <Button
            onClick={() => onShowDeleteConfirm(false)}
            size="sm"
            testId={CONFIGURE_ORG_SSO_DIALOG_DELETE_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.configureOrgSSO.dialog.view.delete.cancel}
          </Button>
        </div>
      </>
    ) : null}
  </>
);
